import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react'

const AudioPlayerContext = createContext(null)

export function useAudioPlayer() {
  const context = useContext(AudioPlayerContext)
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider')
  }
  return context
}

export function AudioPlayerProvider({ children }) {
  const audioRef = useRef(new Audio())
  const [currentSong, setCurrentSong] = useState(null)
  const [currentAlbum, setCurrentAlbum] = useState(null)
  const [playlist, setPlaylist] = useState([])
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [volume, setVolume] = useState(() => {
    const saved = localStorage.getItem('audioPlayerVolume')
    return saved ? parseFloat(saved) : 0.7
  })

  const audio = audioRef.current

  // Set up audio event listeners
  useEffect(() => {
    const handleLoadedMetadata = () => {
      setDuration(audio.duration)
    }

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime)
    }

    const handleEnded = () => {
      playNext()
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    audio.addEventListener('loadedmetadata', handleLoadedMetadata)
    audio.addEventListener('timeupdate', handleTimeUpdate)
    audio.addEventListener('ended', handleEnded)
    audio.addEventListener('play', handlePlay)
    audio.addEventListener('pause', handlePause)

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata)
      audio.removeEventListener('timeupdate', handleTimeUpdate)
      audio.removeEventListener('ended', handleEnded)
      audio.removeEventListener('play', handlePlay)
      audio.removeEventListener('pause', handlePause)
    }
  }, [])

  // Update volume
  useEffect(() => {
    audio.volume = volume
    localStorage.setItem('audioPlayerVolume', volume.toString())
  }, [volume])

  const playSong = useCallback((song, album, songs = []) => {
    if (!song?.mp3Url) return

    setCurrentSong(song)
    setCurrentAlbum(album)
    setPlaylist(songs)

    audio.src = song.mp3Url
    audio.play().catch(err => {
      console.error('Playback error:', err)
    })
  }, [])

  const togglePlay = useCallback(() => {
    if (!currentSong) return

    if (isPlaying) {
      audio.pause()
    } else {
      audio.play().catch(err => {
        console.error('Playback error:', err)
      })
    }
  }, [currentSong, isPlaying])

  const pause = useCallback(() => {
    audio.pause()
  }, [])

  const seek = useCallback((time) => {
    audio.currentTime = time
  }, [])

  const seekPercent = useCallback((percent) => {
    audio.currentTime = (percent / 100) * duration
  }, [duration])

  const playNext = useCallback(() => {
    if (!currentSong || playlist.length === 0) return

    const currentIndex = playlist.findIndex(s => s.id === currentSong.id)
    const nextIndex = (currentIndex + 1) % playlist.length
    const nextSong = playlist[nextIndex]

    if (nextSong) {
      playSong(nextSong, currentAlbum, playlist)
    }
  }, [currentSong, currentAlbum, playlist, playSong])

  const playPrevious = useCallback(() => {
    if (!currentSong || playlist.length === 0) return

    // If more than 3 seconds into song, restart it
    if (currentTime > 3) {
      audio.currentTime = 0
      return
    }

    const currentIndex = playlist.findIndex(s => s.id === currentSong.id)
    const prevIndex = currentIndex <= 0 ? playlist.length - 1 : currentIndex - 1
    const prevSong = playlist[prevIndex]

    if (prevSong) {
      playSong(prevSong, currentAlbum, playlist)
    }
  }, [currentSong, currentAlbum, playlist, currentTime, playSong])

  const value = {
    currentSong,
    currentAlbum,
    playlist,
    isPlaying,
    duration,
    currentTime,
    volume,
    setVolume,
    playSong,
    togglePlay,
    pause,
    seek,
    seekPercent,
    playNext,
    playPrevious
  }

  return (
    <AudioPlayerContext.Provider value={value}>
      {children}
    </AudioPlayerContext.Provider>
  )
}
